import { useMutation } from '@tanstack/react-query';
import { posBackendApi } from '@/lib/api-client';
import type { CustomerImportRow } from '@/lib/customer-import-file';

/**
 * Super-admin customer migration import hooks. Both endpoints live on
 * pos-backend and are super_admin-only; the target tenant is carried in the
 * X-Tenant-Id header (third arg to posBackendApi.post).
 */

export interface ImportRowDisposition {
  row: number;
  phone: string;
  action: 'insert' | 'update' | 'error';
  customerId?: string;
  errors: string[];
  warnings: string[];
}

export interface ImportPreviewResult {
  total: number;
  inserts: number;
  updates: number;
  errors: number;
  warnings: number;
  rows: ImportRowDisposition[];
}

export interface ImportChunkResult {
  inserted: number;
  updated: number;
  errors: string[];
}

export function useCustomerImportPreview() {
  return useMutation({
    // The import page reports errors inline (incl. the CE-not-enabled case);
    // skip the global mutation error toast.
    meta: { suppressGlobalError: true },
    mutationFn: ({ tenantId, rows }: { tenantId: string; rows: CustomerImportRow[] }) =>
      posBackendApi.post<{ data: ImportPreviewResult }>(
        '/customer-engagement/customer-import/preview',
        { rows },
        tenantId,
      ),
  });
}

export function useCustomerImportChunk() {
  return useMutation({
    meta: { suppressGlobalError: true },
    mutationFn: ({
      tenantId,
      rows,
      rowIndexOffset,
    }: {
      tenantId: string;
      rows: CustomerImportRow[];
      rowIndexOffset: number;
    }) =>
      posBackendApi.post<{ data: ImportChunkResult }>(
        '/customer-engagement/customer-import/chunk',
        { rows, rowIndexOffset },
        tenantId,
      ),
  });
}
